import React, {useCallback, useContext, useEffect, useState} from 'react';

import {DateInput, FormInput} from 'src/components/input';
import {useDateValidation} from 'src/hooks/useDateValidation';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import Button from '@pb/kit-component-button';
import CheckBox from '@pb/kit-component-checkbox';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {CURRENCIES, FormattedDate, Integer, RoundedMoney} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import SlideBox, {SlideBoxButton} from '@pb/kit-component-slide-box';
import Table, {Body, Cell, Row} from '@pb/kit-component-table';
import Typography from '@pb/kit-component-typography';
import {LocaleContext} from '@pb/kit-context-locale';

import {MCP_CONTRACT_CELL_WIDTH as WIDTH} from '../constants';

import {ChangeCreditForm} from './change-credits-form';

import {
    mcpContractItem,
    mcpContractItemDetails,
    mcpContractItemDetailsExpanded,
    mcpContractItemExpanded,
    mcpContractItemExpandedDetails,
    mcpContractItemExpired,
    mcpContractItemHandle,
    mcpContractItemMetric,
    mcpContractItemMetrics,
} from './contract-item.module.styl';

import type {Locale} from '@pb/kit-context-locale';
import type {McpContract} from 'src/openapi/admin/models/McpContract';
import type {McpUpdateContractRequest} from 'src/openapi/admin/models/McpUpdateContractRequest';
import type {McpUpdateCreditsRequest} from 'src/openapi/admin/models/McpUpdateCreditsRequest';


type Props = {
    contract: McpContract,
    expanded: boolean,
    onToggle: (contractId: number) => void,
    canEdit: boolean,
    onUpdateContract: (contractId: number, data: McpUpdateContractRequest) => void,
    onUpdateCredits: (contractId: number, data: McpUpdateCreditsRequest) => void,
};

const formatDate = (date: string | null | undefined, locale: Locale): string => {
    if (!date) {
        return '';
    }
    return FormattedDate.format({
        value: date as string,
        format: mapDateFormat(locale.dateFormat, 'NUMERIC'),
    }) || '';
};

const formatCredits = (value: number | null | undefined, locale: Locale): string => {
    if (value == null) {
        return '';
    }
    return Integer.format(value, locale);
};

const formatCost = (value: number | null | undefined, locale: Locale): string => {
    if (value == null) {
        return '-';
    }
    return RoundedMoney.format(value, {...locale, currency: CURRENCIES.USD}, {
        preventRound: true,
        showSymbol: true,
    });
};

export const ContractItem = ({
    contract,
    expanded,
    onToggle,
    canEdit,
    onUpdateContract,
    onUpdateCredits,
}: Props) => {
    const {locale} = useContext(LocaleContext);
    const {mcpContrUpdate} = useEventTracking();

    const [contractNumber, setContractNumber] = useState(contract.contractNumber);
    const [startDate, setStartDate] = useState(contract.startDate);
    const [endDate, setEndDate] = useState(contract.endDate ?? '');

    useEffect(() => {
        setContractNumber(contract.contractNumber);
        setStartDate(contract.startDate);
        setEndDate(contract.endDate ?? '');
    }, [contract]);

    const onToggleDetails = useCallback(() => {
        onToggle(contract.contractId);
    }, [contract.contractId, onToggle]);

    const onChangeStartDate = useCallback((date: string) => setStartDate(date), []);
    const onChangeEndDate = useCallback((date: string) => setEndDate(date), []);

    const {hasInvalidRange} = useDateValidation(startDate, endDate, onChangeEndDate);

    const onSave = useCallback(() => {
        const changedFields: string[] = [];
        if (contractNumber !== contract.contractNumber) {
            changedFields.push('contractNumber');
        }
        if (startDate !== contract.startDate) {
            changedFields.push('startDate');
        }
        if (endDate !== (contract.endDate ?? '')) {
            changedFields.push('endDate');
        }
        mcpContrUpdate({changedFields});
        onUpdateContract(contract.contractId, {
            contractNumber,
            startDate,
            endDate: endDate || undefined,
        });
    }, [contract, contractNumber, startDate, endDate, mcpContrUpdate, onUpdateContract]);

    const onRestore = useCallback(() => {
        setContractNumber(contract.contractNumber);
        setStartDate(contract.startDate);
        setEndDate(contract.endDate ?? '');
    }, [contract]);

    const onApplyCredits = useCallback((data: McpUpdateCreditsRequest) => {
        onUpdateCredits(contract.contractId, data);
    }, [contract.contractId, onUpdateCredits]);

    const isChanged = contractNumber !== contract.contractNumber
        || startDate !== contract.startDate
        || endDate !== (contract.endDate ?? '');
    const canSave = isChanged && contractNumber && startDate && (!contract.endDate || endDate) && !hasInvalidRange;

    const itemClasses = `${mcpContractItem} ${expanded ? mcpContractItemExpanded : ''} ${
        !expanded && contract.dateStatus !== 'ACTIVE' ? mcpContractItemExpired : ''
    }`;

    const detailsClasses = `${mcpContractItemDetails} ${expanded ? mcpContractItemDetailsExpanded : ''}`;

    return (
        <div className={itemClasses}>
            <div className={mcpContractItemHandle}>
                <SlideBoxButton onClick={onToggleDetails}>
                    {slideBoxClick => (
                        <CheckBox
                            name="default"
                            onCheckBoxChange={slideBoxClick}
                            checked={expanded}
                            custom={true}
                            usePropsOnly={true}
                        />
                    )}
                </SlideBoxButton>
            </div>

            <div className={detailsClasses}>
                <Table>
                    <Body>
                        <Row>
                            <Cell width={WIDTH.contract}>
                                {expanded && canEdit ? (
                                    <FormInput
                                        value={contractNumber}
                                        width={WIDTH.contract}
                                        maxLength={255}
                                        onBlur={setContractNumber}
                                    />
                                ) : (
                                    <EllipsisTextHint title={contract.contractNumber}>
                                        {contract.contractNumber}
                                    </EllipsisTextHint>
                                )}
                            </Cell>
                            <Cell {...{className: 'text-align_center', width: WIDTH.startDate}}>
                                {expanded && canEdit ? (
                                    <DateInput
                                        id={`mcp-start-date-${contract.contractId}`}
                                        selectedDate={startDate}
                                        onChange={onChangeStartDate}
                                        clearable={false}
                                    />
                                ) : formatDate(contract.startDate, locale)}
                            </Cell>
                            <Cell {...{className: 'text-align_center', width: WIDTH.endDate}}>
                                {expanded && canEdit ? (
                                    <DateInput
                                        id={`mcp-end-date-${contract.contractId}`}
                                        selectedDate={endDate}
                                        onChange={onChangeEndDate}
                                        clearable={true}
                                    />
                                ) : formatDate(contract.endDate, locale)}
                            </Cell>
                            <Cell {...{className: 'text-align_right', width: WIDTH.credits}}>
                                {formatCredits(contract.initialCredits, locale)}
                            </Cell>
                            <Cell {...{className: 'text-align_right', width: WIDTH.overages}}>
                                {formatCredits(contract.initialOverageCredits, locale)}
                            </Cell>
                            <Cell {...{className: 'text-align_right', width: WIDTH.remainingCredits}}>
                                {formatCredits(contract.remainingCredits, locale)}
                            </Cell>
                            <Cell {...{className: 'text-align_right', width: WIDTH.remainingOverages}}>
                                {formatCredits(contract.remainingOverageCredits, locale)}
                            </Cell>
                        </Row>
                    </Body>
                </Table>

                <SlideBox shown={expanded}>
                    <div className={mcpContractItemExpandedDetails}>
                        <Group direction="v" gap="l" alignItems="start">
                            <div className={mcpContractItemMetrics}>
                                <div className={mcpContractItemMetric}>
                                    <Label>Credit Cost</Label>
                                    <Typography fontWeight="semi-bold">
                                        {formatCost(contract.costPerCredit, locale)}
                                    </Typography>
                                </div>
                                <div className={mcpContractItemMetric}>
                                    <Label>Credits Used</Label>
                                    <Typography fontWeight="semi-bold">
                                        {formatCredits(contract.creditsUsed, locale)}
                                    </Typography>
                                </div>
                                <div className={mcpContractItemMetric}>
                                    <Label>Credits Changed</Label>
                                    <Typography fontWeight="semi-bold">
                                        {formatCredits(
                                            contract.remainingCredits + contract.creditsUsed - contract.initialCredits,
                                            locale,
                                        )}
                                    </Typography>
                                </div>
                                <div className={mcpContractItemMetric}>
                                    <Label>Overage Cost</Label>
                                    <Typography fontWeight="semi-bold">
                                        {formatCost(contract.costPerOverageCredit, locale)}
                                    </Typography>
                                </div>
                                <div className={mcpContractItemMetric}>
                                    <Label>Overage Used</Label>
                                    <Typography fontWeight="semi-bold">
                                        {formatCredits(contract.overageCreditsUsed, locale)}
                                    </Typography>
                                </div>
                            </div>

                            {canEdit ? (
                                <ChangeCreditForm
                                    costPerCredit={contract.costPerCredit}
                                    costPerOverageCredit={contract.costPerOverageCredit}
                                    remainingOverageCredits={contract.remainingOverageCredits}
                                    onApplyCredits={onApplyCredits}
                                />
                            ) : null}

                            {canEdit ? (
                                <Group>
                                    <Button
                                        variant="Type.PRIMARY"
                                        disabled={!canSave}
                                        onClick={onSave}>
                                        Save
                                    </Button>
                                    <Button
                                        variant="Type.SECONDARY"
                                        disabled={!isChanged}
                                        onClick={onRestore}>
                                        Cancel
                                    </Button>
                                </Group>
                            ) : null}
                        </Group>
                    </div>
                </SlideBox>
            </div>
        </div>
    );
};
